/**
 * Utilities for parsing and handling vILT Meeting Creation Status
 *
 * The v_ilt_meeting_creation_status field can have the following values:
 * - "PROCESSING" - Meeting creation is in progress
 * - "COMPLETED" - Meeting was created successfully
 * - "ERROR:<message>" - Meeting creation failed with the specified error message
 */

export type ViltMeetingStatusType =
    | 'processing'
    | 'completed'
    | 'error'
    | 'warning'
    | 'none';

export interface ParsedViltMeetingStatus {
    type: ViltMeetingStatusType;
    message: string | null;
    rawStatus: string | null;
}

const ERROR_PREFIX = 'ERROR:';

/**
 * Parses the v_ilt_meeting_creation_status field to extract status type and error message
 *
 * @param status - The raw v_ilt_meeting_creation_status value
 * @returns Parsed status with type and optional error message
 *
 * @example
 * parseViltMeetingStatus('COMPLETED') // { type: 'completed', message: null, rawStatus: 'COMPLETED' }
 * parseViltMeetingStatus('ERROR:WebEx API failed') // { type: 'error', message: 'WebEx API failed', rawStatus: 'ERROR:WebEx API failed' }
 */
export const parseViltMeetingStatus = (
    status: string | null | undefined,
    meetingId?: string | null,
): ParsedViltMeetingStatus => {
    if (!status) {
        return { type: 'none', message: null, rawStatus: null };
    }

    if (status === 'COMPLETED') {
        return { type: 'completed', message: null, rawStatus: status };
    }

    if (status === 'PROCESSING') {
        return { type: 'processing', message: null, rawStatus: status };
    }

    if (status.startsWith(ERROR_PREFIX)) {
        const rawMessage = status.substring(ERROR_PREFIX.length).trim();
        // Strip machine-generated "VCSP Error NNN: " prefix if present
        const errorMessage = rawMessage.replace(/^VCSP Error \d+:\s*/i, '');
        // If a meeting already exists, this is a cohost/update warning — not a creation failure
        if (meetingId) {
            return {
                type: 'warning',
                message: errorMessage || 'An unknown error occurred',
                rawStatus: status,
            };
        }
        return {
            type: 'error',
            message: errorMessage || 'An unknown error occurred',
            rawStatus: status,
        };
    }

    // Handle any other status as-is (shouldn't happen but provides safety)
    return { type: 'none', message: status, rawStatus: status };
};

/**
 * Returns a user-friendly display message for the vILT meeting status
 */
export const getViltMeetingStatusDisplayText = (
    parsedStatus: ParsedViltMeetingStatus,
): string => {
    switch (parsedStatus.type) {
        case 'completed':
            return 'WebEx meeting created';
        case 'processing':
            return 'Creating WebEx meeting...';
        case 'error':
            return parsedStatus.message || 'WebEx meeting creation failed';
        case 'none':
        default:
            return 'Waiting for WebEx meeting';
    }
};
